import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
interface PrivacyPolicyProps {
  isOpen: boolean;
  onClose: () => void;
}
export function PrivacyPolicy({
  isOpen,
  onClose
}: PrivacyPolicyProps) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);
  return <AnimatePresence data-id="element-363">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.3
    }} className="fixed inset-0 z-50 flex items-start justify-center bg-black/85 backdrop-blur-sm" onClick={onClose} data-id="element-364">
          {/* Close Button */}
          <button className="fixed top-4 right-4 md:top-6 md:right-6 text-white/70 hover:text-white transition-colors z-[60] bg-black/40 p-2 rounded-full backdrop-blur-md" onClick={e => {
        e.stopPropagation();
        onClose();
      }} data-id="element-365">
            <X size={28} data-id="element-366" />
          </button>

          {/* Content Container */}
          <motion.div initial={{
        scale: 0.95,
        opacity: 0,
        y: 20
      }} animate={{
        scale: 1,
        opacity: 1,
        y: 0
      }} exit={{
        scale: 0.95,
        opacity: 0,
        y: 20
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 300
      }} className="relative w-full max-w-3xl mx-4 my-8 md:my-12 max-h-[90vh] overflow-y-auto bg-[#0f1d35] border border-white/10 rounded-2xl shadow-2xl" onClick={e => e.stopPropagation()} data-id="element-367">
            <div className="p-6 md:p-10 lg:p-12" data-id="element-368">
              {/* Header */}
              <h1 className="text-2xl md:text-3xl font-bold text-white mb-2 pr-8" data-id="element-369">
                Ochrana a nakládání s osobními údaji
              </h1>
              <p className="text-[#38bdf8] font-mono text-sm mb-6" data-id="element-370">LKHV.eu</p>
              <div className="border-t border-white/10 mb-6" data-id="element-371" />
              <p className="text-white/50 text-sm italic mb-8" data-id="element-372">
                Datum poslední aktualizace: 10. 02. 2026
              </p>

              {/* Intro */}
              <p className="text-white/70 text-sm leading-relaxed mb-8" data-id="element-373">
                Tento dokument má za cíl poskytnout vám, našim zákazníkům a
                návštěvníkům webových stránek, jasné informace o tom, jak
                Letiště{' '}
                <strong className="text-white" data-id="element-374">Hořovice (AD LKHV)</strong>{' '}
                zpracovává vaše osobní údaje, v souladu s Nařízením Evropského
                parlamentu a Rady (EU) 2016/679 (GDPR).
              </p>

              {/* 1. Správce osobních údajů */}
              <h2 className="text-xl font-bold text-white mb-4" data-id="element-375">
                1. Správce osobních údajů
              </h2>
              <p className="text-white/70 text-sm leading-relaxed mb-4" data-id="element-376">
                Správcem, který určuje účely a prostředky zpracování vašich
                osobních údajů, je:
              </p>
              <ul className="list-disc list-inside text-white/70 text-sm space-y-1.5 mb-8 ml-2" data-id="element-377">
                <li data-id="element-378">
                  <strong className="text-white/90" data-id="element-379">Název společnosti:</strong>{' '}
                  Aviatický klub Tlustice, z. s.
                </li>
                <li data-id="element-380">
                  <strong className="text-white/90" data-id="element-381">Sídlo:</strong> č.p. 258,
                  268 01 Tlustice
                </li>
                <li data-id="element-382">
                  <strong className="text-white/90" data-id="element-383">
                    E-mail pro dotazy GDPR/Kontaktní e-mail:
                  </strong>{' '}
                  info@aviatickyklubtlustice.cz
                </li>
                <li data-id="element-384">
                  <strong className="text-white/90" data-id="element-385">Telefon:</strong> +420 606
                  730 374
                </li>
              </ul>

              {/* 2. Účely a právní základ zpracování */}
              <h2 className="text-xl font-bold text-white mb-4" data-id="element-386">
                2. Účely a právní základ zpracování
              </h2>
              <p className="text-white/70 text-sm leading-relaxed mb-4" data-id="element-387">
                Vaše osobní údaje zpracováváme pouze tehdy, máme-li pro to
                zákonný důvod (právní titul):
              </p>
              <div className="overflow-x-auto -mx-2 px-2 mb-8" data-id="element-388">
                <table className="w-full text-left text-sm min-w-[580px] border border-white/10 rounded-lg overflow-hidden" data-id="element-389">
                  <thead className="bg-[#0a1628]" data-id="element-390">
                    <tr data-id="element-391">
                      <th className="p-3 text-white/60 font-medium border-b border-white/10" data-id="element-392">
                        Účel zpracování (Proč)
                      </th>
                      <th className="p-3 text-white/60 font-medium border-b border-white/10" data-id="element-393">
                        Právní základ (GDPR Článek)
                      </th>
                      <th className="p-3 text-white/60 font-medium border-b border-white/10" data-id="element-394">
                        Kategorie údajů
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5" data-id="element-395">
                    <tr data-id="element-396">
                      <td className="p-3 text-white/70" data-id="element-397">
                        <strong className="text-white/90" data-id="element-398">
                          Plnění smlouvy o službách
                        </strong>{' '}
                        (např. parkování, služby pro privátní uživatele)
                      </td>
                      <td className="p-3 text-white/70" data-id="element-399">
                        <strong className="text-white/90" data-id="element-400">
                          Plnění smlouvy
                        </strong>{' '}
                        (čl. 6 odst. 1 písm. b)
                      </td>
                      <td className="p-3 text-white/70" data-id="element-401">
                        Jméno, příjmení, kontaktní údaje, platební údaje.
                      </td>
                    </tr>
                    <tr data-id="element-402">
                      <td className="p-3 text-white/70" data-id="element-403">
                        <strong className="text-white/90" data-id="element-404">
                          Zajištění letecké a vnitřní bezpečnosti
                        </strong>{' '}
                        (CCTV, kontrola vstupu)
                      </td>
                      <td className="p-3 text-white/70" data-id="element-405">
                        <strong className="text-white/90" data-id="element-406">
                          Plnění právní povinnosti
                        </strong>{' '}
                        (čl. 6 odst. 1 písm. c){' '}
                        <strong className="text-white/90" data-id="element-407">
                          a Oprávněný zájem
                        </strong>{' '}
                        (čl. 6 odst. 1 písm. f)
                      </td>
                      <td className="p-3 text-white/70" data-id="element-408">
                        Vizuální záznamy z kamer, Identifikační údaje při vstupu
                        do neveřejných prostor.
                      </td>
                    </tr>
                    <tr data-id="element-409">
                      <td className="p-3 text-white/70" data-id="element-410">
                        <strong className="text-white/90" data-id="element-411">
                          Plnění zákonných povinností
                        </strong>{' '}
                        (daně, účetnictví, civilní letectví)
                      </td>
                      <td className="p-3 text-white/70" data-id="element-412">
                        <strong className="text-white/90" data-id="element-413">
                          Plnění právní povinnosti
                        </strong>{' '}
                        (čl. 6 odst. 1 písm. c)
                      </td>
                      <td className="p-3 text-white/70" data-id="element-414">
                        Fakturační údaje, archivační údaje dle zákona.
                      </td>
                    </tr>
                    <tr data-id="element-415">
                      <td className="p-3 text-white/70" data-id="element-416">
                        <strong className="text-white/90" data-id="element-417">
                          Marketing a zasílání newsletterů
                        </strong>
                      </td>
                      <td className="p-3 text-white/70" data-id="element-418">
                        <strong className="text-white/90" data-id="element-419">Souhlas</strong> (čl.
                        6 odst. 1 písm. a){' '}
                        <strong className="text-white/90" data-id="element-420">
                          nebo Oprávněný zájem
                        </strong>{' '}
                        (u stávajících zákazníků)
                      </td>
                      <td className="p-3 text-white/70" data-id="element-421">
                        E-mail, případně preference.
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* 3. Specifické zpracování */}
              <h2 className="text-xl font-bold text-white mb-4" data-id="element-422">
                3. Specifické zpracování osobních údajů
              </h2>
              <div className="space-y-4 mb-8" data-id="element-423">
                <div data-id="element-424">
                  <h3 className="text-white/90 font-semibold text-sm mb-1" data-id="element-425">
                    a) Kamerový systém (CCTV)
                  </h3>
                  <p className="text-white/70 text-sm leading-relaxed" data-id="element-426">
                    Na letišti je provozován kamerový systém za účelem ochrany
                    majetku a zajištění bezpečnosti letového provozu. Záznamy
                    jsou uchovávány po dobu nezbytně nutnou (obvykle 72 hodin) a
                    přístup k nim mají pouze oprávněné osoby.
                  </p>
                </div>
                <div data-id="element-427">
                  <h3 className="text-white/90 font-semibold text-sm mb-1" data-id="element-428">
                    b) Cookies a webová analytika
                  </h3>
                  <p className="text-white/70 text-sm leading-relaxed" data-id="element-429">
                    Webové stránky lkhv.eu mohou používat technické cookies
                    nezbytné pro fungování webu. Analytické cookies jsou použity
                    pouze s vaším souhlasem.
                  </p>
                </div>
                <div data-id="element-430">
                  <h3 className="text-white/90 font-semibold text-sm mb-1" data-id="element-431">
                    c) Kontaktní formulář
                  </h3>
                  <p className="text-white/70 text-sm leading-relaxed" data-id="element-432">
                    Pokud nás kontaktujete prostřednictvím e-mailu nebo
                    telefonu, zpracováváme vaše kontaktní údaje za účelem
                    vyřízení vašeho dotazu.
                  </p>
                </div>
              </div>

              {/* 4. Doba zpracování a uložení */}
              <h2 className="text-xl font-bold text-white mb-4" data-id="element-433">
                4. Doba zpracování a uložení
              </h2>
              <p className="text-white/70 text-sm leading-relaxed mb-4" data-id="element-434">
                Osobní údaje uchováváme pouze po dobu nezbytně nutnou k naplnění
                účelu zpracování:
              </p>
              <ul className="list-disc list-inside text-white/70 text-sm space-y-1.5 mb-8 ml-2" data-id="element-435">
                <li data-id="element-436">
                  Smluvní údaje: po dobu trvání smlouvy + zákonná archivační
                  lhůta
                </li>
                <li data-id="element-437">Účetní doklady: 5–10 let dle zákona o účetnictví</li>
                <li data-id="element-438">CCTV záznamy: obvykle 72 hodin</li>
                <li data-id="element-439">Marketingové souhlasy: do odvolání souhlasu</li>
                <li data-id="element-440">
                  Údaje z kontaktních formulářů: po dobu vyřízení dotazu, max. 1
                  rok
                </li>
              </ul>

              {/* 5. Příjemci osobních údajů */}
              <h2 className="text-xl font-bold text-white mb-4" data-id="element-441">
                5. Příjemci osobních údajů
              </h2>
              <p className="text-white/70 text-sm leading-relaxed mb-4" data-id="element-442">
                Vaše osobní údaje mohou být předány následujícím kategoriím
                příjemců:
              </p>
              <ul className="list-disc list-inside text-white/70 text-sm space-y-1.5 mb-8 ml-2" data-id="element-443">
                <li data-id="element-444">
                  Orgány státní správy a dohledu (ÚCL, Policie ČR) v zákonem
                  stanovených případech
                </li>
                <li data-id="element-445">
                  Poskytovatelé IT služeb a hostingu (zpracovatelé na základě
                  smlouvy)
                </li>
                <li data-id="element-446">Účetní a daňoví poradci</li>
              </ul>
              <p className="text-white/70 text-sm leading-relaxed mb-8" data-id="element-447">
                Osobní údaje nejsou předávány do třetích zemí mimo EU/EHP, pokud
                to není nezbytné a zajištěno odpovídajícími zárukami.
              </p>

              {/* 6. Zabezpečení */}
              <h2 className="text-xl font-bold text-white mb-4" data-id="element-448">
                6. Zabezpečení osobních údajů
              </h2>
              <p className="text-white/70 text-sm leading-relaxed mb-8" data-id="element-449">
                Přijali jsme vhodná technická a organizační opatření k ochraně
                vašich osobních údajů před neoprávněným přístupem, ztrátou,
                zničením nebo zneužitím. Přístup k údajům mají pouze oprávněné
                osoby vázané mlčenlivostí.
              </p>

              {/* 7. Vaše práva */}
              <h2 className="text-xl font-bold text-white mb-4" data-id="element-450">
                7. Vaše práva
              </h2>
              <p className="text-white/70 text-sm leading-relaxed mb-4" data-id="element-451">
                V souvislosti se zpracováním osobních údajů máte následující
                práva:
              </p>
              <ul className="list-disc list-inside text-white/70 text-sm space-y-1.5 mb-6 ml-2" data-id="element-452">
                <li data-id="element-453">
                  <strong className="text-white/90" data-id="element-454">Právo na přístup</strong> –
                  máte právo vědět, jaké údaje o vás zpracováváme
                </li>
                <li data-id="element-455">
                  <strong className="text-white/90" data-id="element-456">Právo na opravu</strong> –
                  můžete požádat o opravu nepřesných údajů
                </li>
                <li data-id="element-457">
                  <strong className="text-white/90" data-id="element-458">Právo na výmaz</strong> –
                  můžete požádat o smazání údajů, pokud pominul účel zpracování
                </li>
                <li data-id="element-459">
                  <strong className="text-white/90" data-id="element-460">
                    Právo na omezení zpracování
                  </strong>{' '}
                  – můžete požádat o dočasné omezení zpracování
                </li>
                <li data-id="element-461">
                  <strong className="text-white/90" data-id="element-462">
                    Právo na přenositelnost
                  </strong>{' '}
                  – máte právo získat své údaje ve strukturovaném formátu
                </li>
                <li data-id="element-463">
                  <strong className="text-white/90" data-id="element-464">
                    Právo vznést námitku
                  </strong>{' '}
                  – proti zpracování na základě oprávněného zájmu
                </li>
                <li data-id="element-465">
                  <strong className="text-white/90" data-id="element-466">
                    Právo odvolat souhlas
                  </strong>{' '}
                  – kdykoli, bez vlivu na zákonnost předchozího zpracování
                </li>
              </ul>
              <p className="text-white/70 text-sm leading-relaxed mb-4" data-id="element-467">
                Pro uplatnění svých práv nás kontaktujte na:{' '}
                <strong className="text-[#38bdf8]" data-id="element-468">
                  info@aviatickyklubtlustice.cz
                </strong>
              </p>
              <p className="text-white/70 text-sm leading-relaxed mb-8" data-id="element-469">
                Pokud se domníváte, že zpracování vašich osobních údajů je v
                rozporu s právními předpisy, máte právo podat stížnost u
                dozorového úřadu –{' '}
                <strong className="text-white/90" data-id="element-470">
                  Úřadu pro ochranu osobních údajů (ÚOOÚ)
                </strong>
                , www.uoou.cz.
              </p>

              <div className="border-t border-white/10 pt-6" data-id="element-471">
                <p className="text-white/40 text-xs text-center" data-id="element-472">
                  © 2026 Aviatický klub Tlustice, z.s. Všechna práva vyhrazena.
                </p>
              </div>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}